import { useRef, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deepEqual } from "../helpers/helpers";
import { ReactComponent as ColorPicker } from '../assets/color_wheel.svg';
import facilityThemeService from "../services/facilityThemeService";
import { useDashboardUI } from "../context/GlobalContext";
import { Copy } from "lucide-react";

export const FacilityTheme = () => {
    const { facilityNumber } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [themeConfig, setThemeConfig] = useState([]);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [disabledRevertIndex, setDisabledRevertIndex] = useState(null);
    const [openIndex, setOpenIndex] = useState(0);
    const historyRef = useRef(null);
    const [originalThemeConfig, setOriginalThemeConfig] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [pulsingIndex, setPulsingIndex] = useState(null);
    const { setPageLoader } = useDashboardUI();
    const [copiedValue, setCopiedValue] = useState(null);
    const [isFirstSave, setIsFirstSave] = useState(false);

    //TODO: default theme to be fetched via DB
    const defaultThemeConfig = [
        {
            "display_name": "Primary Button",
            "color_key": "primary",
            "color_value": "231 48% 48%",
            "description": ""
        },
        {
            "display_name": "Primary Button:hover",
            "color_key": "primary-hover",
            "color_value": "231 45% 58%",
            "description": ""
        },
        {
            "display_name": "Primary Button:faded",
            "color_key": "primary-faded",
            "color_value": "229 32% 78%",
            "description": ""
        },
        {
            "display_name": "Active Tab",
            "color_key": "active-tab",
            "color_value": "262 47% 55%",
            "description": ""
        },
        {
            "display_name": "light",
            "color_key": "primary-light",
            "color_value": "231 44% 76%",
            "description": ""
        },
        {
            "display_name": "light-1",
            "color_key": "primary-light-1",
            "color_value": "231 44% 65%",
            "description": ""
        },
        {
            "display_name": "Icons",
            "color_key": "svg",
            "color_value": "259 63% 59%",
            "description": ""
        },
    ]

    // Effect 1: Fetch facility theme on mount or facilityNumber change
    useEffect(() => {
        const fetchTheme = async () => {
            try {
                const data = await facilityThemeService.getFacilityThemeById(facilityNumber);

                if (!data?.theme_config) {
                    setIsFirstSave(true);
                    toast.info("No theme exists for this facility. Loading default theme.");
                    const converted = convertColorValue(defaultThemeConfig);
                    setThemeConfig(converted);
                    setOriginalThemeConfig(converted);
                    setHistory([]);
                } else if (data?.theme_config && Array.isArray(data.theme_config)) {
                    setIsFirstSave(false);
                    setHistory(data?.history || []);
                    // Convert HSL -> HEX for UI display
                    const converted = convertColorValue(data.theme_config);
                    setThemeConfig(converted);
                    setOriginalThemeConfig(converted);
                } else {
                    setIsFirstSave(false);
                    toast.warn("Invalid theme data format received.");
                }
            } catch (error) {
                console.error("Error fetching facility theme:", error);
                toast.error("Failed to load facility theme.");
            } finally {
                setLoading(false);
            }
        };

        fetchTheme();
    }, [facilityNumber]);

    // Effect 2: Show History and smooth scroll on View History click
    useEffect(() => {
        if (showHistory && historyRef.current) {
            historyRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [showHistory]);

    // Effect 3: Compute Changes and enable/disable Save button
    useEffect(() => {
        setHasChanges(!deepEqual(themeConfig, originalThemeConfig));
    }, [themeConfig, originalThemeConfig]);

    // Effect 4: Disable revert version button from history if clicked
    useEffect(() => {
        setDisabledRevertIndex(null);
    }, [history]);

    const handleChange = (index, key, value) => {
        setThemeConfig((prev) => {
            const updated = prev.map((item, i) =>
                i === index ? { ...item, [key]: value } : item
            );
            return updated;
        });
    };

    const convertColorValue = (data) => {
        return data.map((item) => ({
            ...item,
            color_value: hslToHex(item.color_value),
        }));
    }

    const handleSave = async () => {
        try {
            setPageLoader(true);
            // Convert display_color (hex) back to HSL for backend
            const dataToSave = themeConfig.map(({ inputRef, ...item }) => ({
                ...item,
                color_value: hexToHsl(item.color_value),
            }));
            let resp;
            if (isFirstSave) {
                resp = await facilityThemeService.createFacilityTheme({
                facility_id: parseInt(facilityNumber),
                theme_config: dataToSave,
            });
            toast.success("Theme saved!");
            setIsFirstSave(false);
            } else {
                resp = await facilityThemeService.updateFacilityTheme(facilityNumber, {
                    theme_config: dataToSave,
                });
                toast.success("Theme saved!");
                setHistory(resp.history || []);
            }
            setPageLoader(false);
            const converted = convertColorValue(resp.theme_config);
            setThemeConfig(converted);
            setOriginalThemeConfig(converted);
        } catch (error) {
            setPageLoader(false);
            console.error("Error saving theme config:", error);
            toast.error("Failed to save Theme.");
        }
    };

    const handleRevert = (config, index) => {
        // Convert the saved HSL values back to HEX for editing view
        const converted = config.map((item) => ({
            ...item,
            color_value: hslToHex(item.color_value),
        }));
        setThemeConfig(converted);
        // disable this button, enable all others
        setDisabledRevertIndex(index);
        toast.info("Reverted to selected theme version. Don't forget to save!");
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);
    };

    const handlePreviewClick = (index) => {
        setPulsingIndex(index);
        setTimeout(() => setPulsingIndex(null), 400); // same as animation duration
    };

    const handleCopyColor = (value) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopiedValue(value);
            toast.info(`Copied ${value} to clipboard!`, { autoClose: 800 });
            setTimeout(() => setCopiedValue(null), 800);
        })
            .catch(() => toast.error("Failed to copy color value."));
    };

    if (loading) {
        return <p className="text-center mt-10">Loading theme configuration...</p>;
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-10">
            {/* Edit Theme */}
            <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-8 mt-8">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">Facility Theme</h2>
                    <button
                        onClick={() => navigate("/facility")}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                    >
                        ← Back
                    </button>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-3 gap-4 mb-3 text-sm font-semibold text-gray-600">
                    <span>Component</span>
                    <span>Hex Code</span>
                    <span className="text-center">Preview</span>
                </div>

                {/* Color list */}
                <div className="space-y-3">
                    {themeConfig.map((color, index) => {
                        return (
                            <div
                                key={index}
                                className="grid grid-cols-3 items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition"
                            >
                                {/* Display Name */}
                                <div>
                                    <input
                                        type="text"
                                        value={color.display_name}
                                        onChange={(e) => handleChange(index, "display_name", e.target.value)}
                                        disabled
                                        readOnly
                                        className="w-full px-2 py-1 border rounded text-sm font-medium"
                                    />
                                </div>

                                {/* Color Inputs */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={color.color_value}
                                        onChange={(e) => handleChange(index, "color_value", e.target.value)}
                                        className="flex-1 px-2 py-1 border rounded text-sm"
                                    />
                                    <div className="relative">
                                        {/* Hidden color input */}
                                        <input
                                            type="color"
                                            value={color.color_value}
                                            onChange={(e) => handleChange(index, "color_value", e.target.value)}
                                            ref={(el) => (color.inputRef = el)} // store ref for programmatic click
                                            className="absolute w-0 h-0 opacity-0"
                                        />
                                        {/* Clickable SVG */}
                                        <ColorPicker
                                            onClick={() => color.inputRef.click()}
                                            className={`cursor-pointer p-1 w-9 h-9 transition-transform duration-200 ${pulsingIndex === index ? 'animate-pulseOnce' : ''
                                                }`}
                                        />
                                    </div>
                                </div>

                                {/* Preview */}
                                <div
                                    className="h-8 rounded border"
                                    style={{ backgroundColor: color.color_value }}
                                    onClick={() => handlePreviewClick(index)}
                                ></div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-end items-center mt-8 gap-4">
                    {/* View History Toggle */}
                    {history && history.length > 0 && (
                        <button
                            onClick={() => setShowHistory((prev) => !prev)}
                            className="px-6 py-2 border border-purple-500 text-purple-600 rounded-full font-semibold shadow hover:bg-purple-50 transition"
                        >
                            {showHistory ? "Hide History" : "View History"}
                        </button>
                    )}
                    {/* Save button */}
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges}
                        className={`px-6 py-2 rounded-full font-semibold shadow transition 
                            ${hasChanges
                                ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:opacity-95 cursor-pointer"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                    >
                        Save Theme
                    </button>
                </div>
            </div>
            {/* History */}
            {showHistory && (<div ref={historyRef} className="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-8 mt-8">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">Edit History</h2>
                </div>
                {history.map((entry, idx) => {
                    const isOpen = openIndex === idx;
                    return (
                        <div key={idx} style={{ marginBottom: "5px" }}>
                            <div
                                onClick={() => setOpenIndex(isOpen ? null : idx)}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "5px 10px",
                                    borderRadius: "3px",
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    {/* <span>{`Updated by: ${entry.updated_by}`}</span> */}
                                    <span>{`Updated at: ${new Date(entry.updated_at.concat('Z')).toLocaleString()}`}</span>
                                </div>
                                <span style={{ display: "flex", alignItems: "center", transition: "transform 0.3s" }}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        style={{
                                            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s",
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M9 6l6 6l-6 6"></path>
                                    </svg>
                                </span>
                            </div>

                            {isOpen && (
                                <div
                                    style={{
                                        padding: "10px",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    {/* Table Header */}
                                    <div className="grid grid-cols-3 gap-4 mb-3 text-sm font-semibold text-gray-600">
                                        <span>Color Name</span>
                                        <span>Value</span>
                                        <span className="text-center">Preview</span>
                                    </div>

                                    {/* Color list */}
                                    <div className="space-y-3">
                                        {entry.theme_config.map((color, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className="grid grid-cols-3 items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition"
                                                >
                                                    {/* Display Name */}
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={color.display_name}
                                                            readOnly
                                                            disabled
                                                            className="w-full px-2 py-1 border rounded text-sm font-medium"
                                                        />
                                                    </div>

                                                    {/* Color Inputs */}
                                                    <div
                                                        onClick={() => handleCopyColor(hslToHex(color.color_value))}
                                                        className="flex items-center justify-between gap-2 px-2 py-1 border rounded text-sm bg-gray-50 text-gray-700 cursor-pointer hover:bg-purple-50 hover:text-purple-700 transition group"
                                                        title="Click to copy color value"
                                                    >
                                                        <span className="truncate">{hslToHex(color.color_value)}</span>
                                                        <Copy
                                                            size={16}
                                                            className={`transition ${copiedValue === hslToHex(color.color_value)
                                                                    ? "text-purple-600 scale-110"
                                                                    : "text-gray-400 group-hover:text-purple-500"
                                                                }`}
                                                        />
                                                    </div>

                                                    {/* Preview */}
                                                    <div
                                                        className="h-8 rounded border"
                                                        style={{ backgroundColor: hslToHex(color.color_value) }}
                                                    ></div>
                                                </div>
                                            );
                                        })}
                                        {/* Revert Button */}
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            marginTop: "16px",
                                            marginBottom: "8px",
                                            paddingRight: "8px",
                                        }}>
                                            <button
                                                onClick={() => handleRevert(entry.theme_config, idx)}
                                                disabled={disabledRevertIndex === idx}
                                                className={`px-6 py-2 rounded-full font-semibold shadow transition 
                                                        ${disabledRevertIndex === idx
                                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                        : "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:opacity-95 cursor-pointer"}`}
                                            >
                                                Revert to this version
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
}

// --- Utility: Convert HEX → HSL ---
function hexToHsl(hex) {
    hex = hex.replace("#", "");
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// --- Utility: Convert HSL → HEX ---
function hslToHex(hslString) {
    const parts = hslString.split(" ");
    if (parts.length < 3) return "#000000";
    let [h, s, l] = parts.map((v) => parseFloat(v));
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (0 <= h && h < 60) [r, g, b] = [c, x, 0];
    else if (60 <= h && h < 120) [r, g, b] = [x, c, 0];
    else if (120 <= h && h < 180) [r, g, b] = [0, c, x];
    else if (180 <= h && h < 240) [r, g, b] = [0, x, c];
    else if (240 <= h && h < 300) [r, g, b] = [x, 0, c];
    else if (300 <= h && h < 360) [r, g, b] = [c, 0, x];
    const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}